import PageError from '../components/PageError';
import {
  Users,
  User,
  UserCheck,
  UserX,
  Phone,
  Search,
  Filter,
  RefreshCw,
  Mail,
  XCircle,
  MoreHorizontal,
  Edit,
  Trash2,
  Download,
  Clock,
  Bell,
  BellOff,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { exportToCsv } from '../utils/exportCsv';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import ParentFormModal from '../components/ParentFormModal';
import DeleteImpactModal from '../components/DeleteImpactModal';
import { useFeatureFlags } from '../hooks/useFeatureFlags.jsx';
import useParents from '../hooks/trParents';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useSortPaginate } from '../hooks/useSortPaginate';
import { SortableHeader, TablePagination, ColumnToggle, useColumnVisibility } from '../components/TableControls';
import { StatusBadge } from '../components/ui/StatusBadge';

const LastLoginBadge = ({ date }) => {
  if (!date) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400">
        <Clock size={10} />
        Jamais
      </span>
    );
  }
  const d = new Date(date);
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  const colorClass =
    diffDays < 7
      ? 'bg-green-100 text-green-700'
      : diffDays < 30
        ? 'bg-blue-100 text-blue-700'
        : 'bg-amber-100 text-amber-700';
  const label = formatDistanceToNow(d, { addSuffix: true, locale: fr });
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      title={d.toLocaleString('fr-FR')}
    >
      <Clock size={10} />
      {label}
    </span>
  );
};

const NotifPermissionBadge = ({ status }) => {
  if (status === 'authorized') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <Bell size={10} />
        Activées
      </span>
    );
  }
  if (status === 'denied') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <BellOff size={10} />
        Désactivées
      </span>
    );
  }
  if (status === 'provisional') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Bell size={10} />
        Provisoires
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400">
      <Bell size={10} />—
    </span>
  );
};

const PARENT_COLUMNS = [
  { key: 'contact', label: 'Contact' },
  { key: 'enfants', label: 'Enfants' },
  { key: 'status', label: 'Statut' },
];

const ParentManagementUnified = () => {
  const { hasFeature } = useFeatureFlags();
  const {
    filteredParents,
    loading,
    error,
    stats,
    showModal,
    editingParent,
    deleteModalOpen,
    parentToDelete,
    filters,
    updateFilters,
    setShowModal,
    setDeleteModalOpen,
    loadParents,
    handleEdit,
    handleDeleteParent,
    confirmDeleteParent,
    handleSave,
    isImpersonating,
  } = useParents();

  const { visibility, toggle, visible } = useColumnVisibility({
    contact: true,
    enfants: true,
    status: true,
  });

  const { paginated, page, totalPages, totalItems, pageSize, sortCol, sortDir, setPage, toggleSort } = useSortPaginate(
    filteredParents,
    { col: 'last_name', dir: 'asc' },
    15,
    [filters],
  );

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Parents</h1>
          <p className="text-gray-500">Gérez les informations des parents et tuteurs</p>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() =>
              exportToCsv(
                filteredParents,
                [
                  { label: 'prenom_parent', value: (p) => p.first_name || '' },
                  { label: 'nom_parent', value: (p) => p.last_name || '' },
                  { label: 'telephone_parent', value: (p) => p.phone || '' },
                  { label: 'email_parent', value: (p) => p.email || '' },
                  {
                    label: 'relation_parent',
                    value: (p) => p.relationship || '',
                  },
                  { label: 'adresse', value: (p) => p.address || '' },
                  { label: 'statut', value: (p) => p.status || '' },
                  {
                    label: 'compte_active',
                    value: (p) => (p.is_first_login === false ? 'oui' : 'non'),
                  },
                  {
                    label: 'nb_enfants',
                    value: (p) => p.children?.length ?? 0,
                  },
                ],
                'parents.csv',
              )
            }
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </Button>
          <div className="relative group inline-flex">
            <button
              type="button"
              disabled
              className="flex items-center justify-center px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed text-sm font-medium"
            >
              Nouveau Parent
            </button>
            <div
              className="absolute top-full mt-2 right-0 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-75 pointer-events-none z-20"
              role="tooltip"
            >
              La création d'un parent se fait lors de l'ajout d'un élève.
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      {hasFeature('metrics') && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              icon: Users,
              color: 'indigo',
              label: 'Total Parents',
              value: stats.total_parents || 0,
            },
            {
              icon: UserCheck,
              color: 'green',
              label: 'Actifs',
              value: stats.active_parents || 0,
            },
            {
              icon: UserX,
              color: 'red',
              label: 'Inactifs',
              value: stats.inactive_parents || 0,
            },
            {
              icon: Phone,
              color: 'blue',
              label: 'Contacts Vérifiés',
              value: stats.verified_contacts || 0,
            },
          ].map(({ icon: Icon, color, label, value }) => (
            <div
              key={label}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center space-x-4"
            >
              <div className={`p-3 bg-${color}-100 rounded-lg text-${color}-600`}>
                <Icon size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barre de filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone..."
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {filters.search && (
              <button
                onClick={() => updateFilters({ search: '' })}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                {
                  value: '',
                  label: 'Tous',
                  active: 'bg-blue-100 text-blue-800',
                },
                {
                  value: 'active',
                  label: 'Actifs',
                  active: 'bg-green-100 text-green-800',
                },
                {
                  value: 'inactive',
                  label: 'Inactifs',
                  active: 'bg-gray-100 text-gray-800',
                },
              ].map(({ value, label, active }) => (
                <button
                  key={label}
                  onClick={() => updateFilters({ status: value })}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters.status === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadParents}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
            <ColumnToggle columns={PARENT_COLUMNS} visibility={visibility} onToggle={toggle} />
          </div>
        </div>
      </div>

      {/* Tableau / Cards — parents */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && <PageError error={error} onRetry={loadParents} variant="inline" />}
        {!error && (
          <>
            {/* ── Vue cartes (mobile < md) ── */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={`skel-m-${i}`} className="p-4 flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))
              ) : filteredParents.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun parent trouvé</h3>
                  <p className="text-sm text-gray-500">
                    {filters.search || filters.status
                      ? 'Aucun parent ne correspond aux critères de recherche.'
                      : "Les parents sont créés automatiquement lors de l'ajout d'un élève."}
                  </p>
                </div>
              ) : (
                paginated.map((parent) => (
                  <motion.div
                    key={parent.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <User className="text-blue-600" size={20} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {parent.first_name} {parent.last_name}
                          </div>
                          {parent.ref && <div className="text-xs text-gray-400 truncate">{parent.ref}</div>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <StatusBadge status={parent.status} />
                        {parent.is_first_login === false ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Activé
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            En attente
                          </span>
                        )}
                        {isImpersonating && <LastLoginBadge date={parent.last_login_at} />}
                        {isImpersonating && <NotifPermissionBadge status={parent.notifications_permission_status} />}
                      </div>
                    </div>
                    <div className="mt-2.5 ml-[52px] space-y-1.5">
                      {parent.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone size={13} className="text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-600">{parent.phone}</span>
                        </div>
                      )}
                      {parent.email && (
                        <div className="flex items-center gap-1.5">
                          <Mail size={13} className="text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-600 truncate max-w-[220px]">{parent.email}</span>
                        </div>
                      )}
                      {parent.children?.length > 0 ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Users size={13} className="text-gray-400 shrink-0" />
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {parent.children.length} enfant
                            {parent.children.length > 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-gray-500 truncate max-w-[180px]">
                            {parent.children.slice(0, 2).map((c, i) => (
                              <span key={c.id || i} className="inline-flex items-center gap-1">
                                {i > 0 && ', '}
                                {c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                                {c.status === 'inactive' && (
                                  <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                    Inactif
                                  </span>
                                )}
                              </span>
                            ))}
                            {parent.children.length > 2 ? ` +${parent.children.length - 2}` : ''}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* ── Tableau (desktop ≥ md) ── */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-[700px] w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader col="last_name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>
                      Parent
                    </SortableHeader>
                    {visible('contact') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                    )}
                    {visible('enfants') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enfants
                      </th>
                    )}
                    {visible('status') && (
                      <SortableHeader
                        col="status"
                        sortCol={sortCol}
                        sortDir={sortDir}
                        onSort={toggleSort}
                        className="w-[1%] whitespace-nowrap"
                      >
                        Statut
                      </SortableHeader>
                    )}
                    <SortableHeader
                      col="is_first_login"
                      sortCol={sortCol}
                      sortDir={sortDir}
                      onSort={toggleSort}
                      className="w-[1%] whitespace-nowrap"
                    >
                      Application
                    </SortableHeader>
                    {isImpersonating && (
                      <th className="px-4 py-3 w-[1%] whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dernière connexion
                      </th>
                    )}
                    {isImpersonating && (
                      <th className="px-4 py-3 w-[1%] whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notifs
                      </th>
                    )}
                    <th className="px-4 py-3 w-[1%] whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Skeleton className="w-10 h-10 rounded-full" />
                            <Skeleton className="h-3.5 w-28" />
                          </div>
                        </td>
                        {visible('contact') && (
                          <td className="px-4 py-4">
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-32" />
                              <Skeleton className="h-3 w-24" />
                            </div>
                          </td>
                        )}
                        {visible('enfants') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-5 w-16 rounded-full" />
                          </td>
                        )}
                        {visible('status') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-5 w-14 rounded-full" />
                          </td>
                        )}
                        <td className="px-4 py-4">
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end">
                            <Skeleton className="h-7 w-7 rounded" />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : filteredParents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun parent trouvé</h3>
                        <p className="text-sm text-gray-500">
                          {filters.search || filters.status
                            ? 'Aucun parent ne correspond aux critères de recherche.'
                            : "Les parents sont créés automatiquement lors de l'ajout d'un élève."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    paginated.map((parent) => (
                      <motion.tr
                        key={parent.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <User className="text-blue-600" size={20} />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {parent.first_name} {parent.last_name}
                              </div>
                              {parent.ref && <div className="text-sm text-gray-400">{parent.ref}</div>}
                            </div>
                          </div>
                        </td>
                        {visible('contact') && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center mb-1">
                                <Phone size={14} className="mr-2 text-gray-400" />
                                {parent.phone}
                              </div>
                              <div className="flex items-center">
                                <Mail size={14} className="mr-2 text-gray-400" />
                                {parent.email}
                              </div>
                            </div>
                          </td>
                        )}
                        {visible('enfants') && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 space-y-1">
                              {parent.children && parent.children.length > 0 ? (
                                <>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {parent.children.length} enfant
                                    {parent.children.length > 1 ? 's' : ''}
                                  </span>
                                  <div className="text-xs text-gray-600">
                                    {parent.children.slice(0, 2).map((child, index) => (
                                      <div key={child.id || index} className="flex items-center gap-1 flex-wrap">
                                        <span>
                                          {child.full_name ||
                                            `${child.first_name || ''} ${child.last_name || ''}`.trim()}
                                        </span>
                                        {child.class_name && (
                                          <span>
                                            {' '}
                                            — {child.class_name}
                                            {child.grade_level ? ` (${child.grade_level})` : ''}
                                          </span>
                                        )}
                                        {child.status === 'inactive' && (
                                          <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                            Inactif
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    {parent.children.length > 2 && (
                                      <div className="text-[11px] text-gray-400">
                                        +{parent.children.length - 2} autre(s)
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-400 italic">Aucun enfant</span>
                              )}
                            </div>
                          </td>
                        )}
                        {visible('status') && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <StatusBadge status={parent.status} />
                          </td>
                        )}
                        <td className="px-4 py-4 whitespace-nowrap">
                          {parent.is_first_login === false ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Activé
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              En attente
                            </span>
                          )}
                        </td>
                        {isImpersonating && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <LastLoginBadge date={parent.last_login_at} />
                          </td>
                        )}
                        {isImpersonating && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <NotifPermissionBadge status={parent.notifications_permission_status} />
                          </td>
                        )}
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                aria-label="Actions"
                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => handleEdit(parent)}>
                                <Edit size={14} className="mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteParent(parent)}
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loading && filteredParents.length > 0 && (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {showModal && (
        <ParentFormModal
          isOpen={true}
          parent={editingParent}
          mode={editingParent ? 'edit' : 'create'}
          onSubmit={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}

      {deleteModalOpen && parentToDelete && (
        <DeleteImpactModal
          entity={parentToDelete}
          entityType="parent"
          entityName={`${parentToDelete.first_name || ''} ${parentToDelete.last_name || ''}`.trim()}
          onConfirm={confirmDeleteParent}
          onCancel={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ParentManagementUnified;
